/**
 * Custody movements that stopped and are waiting for a person.
 *
 * ## Why this page exists
 *
 * When a movement's outcome is genuinely unknown - the custodian was asked and gave no definite
 * answer - the executor stops at `needs_review` rather than guess. That is the right behaviour:
 * guessing "it moved" loses the coin from our books, guessing "it did not" refunds a customer
 * for a transfer that already left. But it left a hole. The system parked a money decision for
 * a human and gave that human nowhere to go, so a movement could sit unanswered for ever.
 *
 * This page is that somewhere. It decides nothing. An operator opens the custodian's own
 * dashboard, finds out what really happened, and records it here.
 *
 * ## The two answers are not equal
 *
 * **"It moved"** closes the movement and nothing else happens.
 *
 * **"It did not move"** is the answer that spends money if it is wrong: it is what tells the
 * rest of the platform the coin is still ours, which is what permits a debit to be reversed.
 * The copy says so plainly rather than presenting two symmetrical buttons, because the cost of
 * the two mistakes is not symmetrical.
 *
 * The reference the custodian gave is the most useful thing on the row - it is what an operator
 * searches for on the other side. A movement with none is the hard case and is shown as such.
 */
import { useCallback, useEffect, useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import type { StuckMovement } from '../contracts/types.js';
import { Badge, Button, Card, ErrorBox, LoadingState } from '../components/ui.js';

/** What each status means, in an operator's words rather than the system's. */
const STATUS_COPY: Readonly<Record<string, string>> = {
  needs_review:
    'We do not know whether this moved. The custodian was asked and did not give a definite answer, so nothing was assumed.',
  failed: 'The custodian refused it. Nothing moved.',
};

export function TreasuryStuckMovementsPage(): JSX.Element {
  const { api } = useAuth();
  const [movements, setMovements] = useState<StuckMovement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Which row is open for a decision. One at a time: this is not bulk work. */
  const [openId, setOpenId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const result = await api.stuckMovements();
      setMovements(result.movements);
    } catch (cause) {
      setError(messageOf(cause));
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const close = (): void => {
    setOpenId(null);
    setNote('');
    setReference('');
  };

  const resolve = async (id: string, outcome: 'completed' | 'failed'): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const trimmedReference = reference.trim();
      await api.resolveMovement(id, {
        outcome,
        note: note.trim(),
        ...(trimmedReference.length === 0 ? {} : { providerReference: trimmedReference }),
      });
      close();
      // Re-read rather than removing the row locally: the server is the authority on what is
      // still waiting, and another operator may have resolved something while this was open.
      await load();
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setSaving(false);
    }
  };

  if (movements === null && error === null) return <LoadingState />;

  return (
    <Card title="Movements waiting for a decision">
      <p className="muted">
        Custody movements the system could not settle on its own. Check the custodian, then record
        what you found.
      </p>

      {error !== null && <ErrorBox message={error} />}

      {movements !== null && movements.length === 0 && (
        <p className="muted">Nothing is waiting. Every custody movement has settled.</p>
      )}

      {movements?.map((movement) => {
        const isOpen = openId === movement.id;
        // Eight characters, matching the server's floor, so the button does not enable on a
        // note nobody could act on later.
        const canSave = note.trim().length >= 8 && !saving;

        return (
          <div key={movement.id} style={{ borderTop: '1px solid var(--line)', padding: '14px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Badge tone={movement.status === 'needs_review' ? 'amber' : 'red'}>
                {movement.status === 'needs_review' ? 'Unknown' : 'Refused'}
              </Badge>
              <strong>
                {movement.amount} {movement.currency}
              </strong>
              {movement.network !== null && <span className="muted">on {movement.network}</span>}
              <span className="muted">
                {movement.kind} at {movement.provider}
              </span>
            </div>

            <p className="muted" style={{ marginTop: 6 }}>
              {STATUS_COPY[movement.status] ?? movement.status}
            </p>

            {movement.providerReference === null ? (
              <p className="muted">
                The custodian gave no reference for this one, so it has to be found by amount and
                time on their side.
              </p>
            ) : (
              <p>
                Their reference: <strong>{movement.providerReference}</strong>
              </p>
            )}

            {movement.lastError !== null && (
              <p className="muted">Reported: {movement.lastError}</p>
            )}

            {!isOpen && (
              <Button
                onClick={() => {
                  setOpenId(movement.id);
                  setNote('');
                  setReference(movement.providerReference ?? '');
                }}
              >
                Record what happened
              </Button>
            )}

            {isOpen && (
              <div style={{ marginTop: 10 }}>
                <label htmlFor={`note-${movement.id}`}>
                  What did the custodian show? This is saved to the audit log, so do not put
                  anything secret in it.
                </label>
                <textarea
                  id={`note-${movement.id}`}
                  value={note}
                  rows={3}
                  onChange={(event) => setNote(event.target.value)}
                  style={{ width: '100%', marginTop: 6 }}
                  placeholder="e.g. Found it on the NOWPayments dashboard, status completed."
                />

                <label htmlFor={`ref-${movement.id}`} style={{ marginTop: 10, display: 'block' }}>
                  Their reference, if you found one
                </label>
                <input
                  id={`ref-${movement.id}`}
                  value={reference}
                  onChange={(event) => setReference(event.target.value)}
                  style={{ width: '100%', marginTop: 6 }}
                />

                <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                  <Button disabled={!canSave} onClick={() => void resolve(movement.id, 'completed')}>
                    {saving ? 'Saving…' : 'The money moved'}
                  </Button>
                  {/*
                    Deliberately not styled as an equal twin of the button above. This is the
                    answer that authorises putting the balance back, so it is the one that
                    should feel like a decision.
                  */}
                  <Button
                    variant="danger"
                    disabled={!canSave}
                    onClick={() => void resolve(movement.id, 'failed')}
                  >
                    {saving ? 'Saving…' : 'It did not move'}
                  </Button>
                  <Button variant="ghost" disabled={saving} onClick={close}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </Card>
  );
}
